import { useEffect, useRef, useState } from 'preact/hooks';
import { MAX_CHAT_LENGTH, type ChatMessage } from '../shared/protocol';
import { useSeatColor } from './colors';

export interface ChatProps {
  messages: readonly ChatMessage[];
  /** The viewer's seat; their own lines are marked, and only seated players can post. */
  seat: number | null;
  send: (text: string) => void;
}

/**
 * Room chat. While the panel is scrolled out of view (the usual case on a
 * phone, where it sits below the board), a new line from someone else pops a
 * pill that scrolls back to it.
 */
export function ChatPanel({ messages, seat, send }: ChatProps) {
  const [draft, setDraft] = useState('');
  const seatColor = useSeatColor();
  const [visible, setVisible] = useState(true);
  const [unseen, setUnseen] = useState<ChatMessage | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const lastId = useRef(messages[messages.length - 1]?.id ?? 0);

  useEffect(() => {
    const el = panel.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setVisible(!!entry?.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const newest = messages[messages.length - 1];
    if (list.current) list.current.scrollTop = list.current.scrollHeight;
    if (!newest || newest.id <= lastId.current) return;
    lastId.current = newest.id;
    if (newest.seat !== seat && !visible) setUnseen(newest);
  }, [messages]);

  useEffect(() => {
    if (visible) setUnseen(null);
  }, [visible]);

  return (
    <div class="panel chat" ref={panel} data-testid="chat">
      <h2>Chat</h2>
      <ol ref={list} aria-live="polite" data-testid="chat-log">
        {messages.length === 0 && <li class="muted">No messages yet.</li>}
        {messages.map((m) => (
          <li key={m.id} class={m.seat === seat ? 'mine' : ''}>
            <span class="swatch" style={{ background: seatColor(m.seat) }} />
            <strong>{m.name}</strong> <span class="chat-text">{m.text}</span>
          </li>
        ))}
      </ol>
      {seat !== null ? (
        <form
          class="row"
          onSubmit={(e) => {
            e.preventDefault();
            const text = draft.trim();
            if (!text) return;
            send(text);
            setDraft('');
          }}
        >
          <input
            value={draft}
            maxLength={MAX_CHAT_LENGTH}
            placeholder="Say something"
            aria-label="Chat message"
            data-testid="chat-input"
            onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          />
          <button type="submit" class="secondary" data-testid="chat-send" disabled={!draft.trim()}>
            Send
          </button>
        </form>
      ) : (
        <p class="muted small">Take a seat to chat.</p>
      )}
      {unseen && (
        <button
          type="button"
          class="chat-pill"
          data-testid="chat-pill"
          onClick={() => {
            panel.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setUnseen(null);
          }}
        >
          <strong>{unseen.name}:</strong> {unseen.text.length > 60 ? `${unseen.text.slice(0, 60)}…` : unseen.text}
        </button>
      )}
    </div>
  );
}
